import { Observable } from "rxjs";

import { APP_ENV } from "config/globals";
import { ApiService } from "lib/services";
import { AccountRepository } from "lib/repositories/index";
import st from "lib/utils/index";
import { INITIALIZE_APP, createAlert, removeAlert } from "modules/app/actions";

import LoginViewDuck from "./LoginView/index";
import SendResetPasswordInstructionsViewDuck from "./SendResetPasswordInstructionsView/index";
import ResetPasswordViewDuck from "./ResetPasswordView/index";
import SendConfirmationInstructionsViewDuck from "./SendConfirmationInstructionsView/index";
import ConfirmEmailViewDuck from "./ConfirmEmailView/index";
import SendUnlockInstructionsViewDuck from "./SendUnlockInstructionsView/index";
import UnlockAccessViewDuck from "./UnlockAccessView/index";

import {
  stateSelector,
  companyInstance,
  productsInstance,
  SET_CURRENT_USER,
  CHANGE_CURRENT_COMPANY,
  FETCH_CURRENT_USER,
  FETCH_CURRENT_USER_SUCCESS,
  FETCH_CURRENT_USER_FAILURE,
  FETCH_PRODUCTS_START,
  FETCH_PRODUCTS_SUCCESS,
  FETCH_PRODUCTS_FAILURE,
  FETCH_CURRENT_COMPANY_START,
  FETCH_CURRENT_COMPANY_SUCCESS,
  FETCH_CURRENT_COMPANY_FAILURE,
  LOGOUT,
  fetchCurrentUser,
  fetchCurrentCompany,
  setCurrentUser,
  changeCurrentCompany,
  fetchProducts,
  logout,
} from "./actions";
export * from "./actions";

const setLocale = (locale) => {
  I18n.locale = locale;
  moment.locale(locale);
  ApiService.setLocale(locale);
};

const LOCALE_CACHE_KEY = "socialtravel-partnerapp-locale";
const cachedLocale = window.localStorage.getItem(LOCALE_CACHE_KEY);
if (cachedLocale) {
  setLocale(cachedLocale);
} else {
  const userLocales = (navigator.languages || []).map((s) => s.slice(0, 2));
  const detectedLocale = userLocales.find((l) => I18n.availableLocales.includes(l)) || "en";
  setLocale(detectedLocale);
}

const CURRENT_USER_EMAIL_CACHE_KEY = `socialtravel-partnerapp-${APP_ENV}/auth/user.email`;
const CURRENT_USER_AUTH_TOKEN_CACHE_KEY = `socialtravel-partnerapp-${APP_ENV}/auth/user.auth_token`;
const CURRENT_COMPANY_ID_CACHE_KEY = `socialtravel-partnerapp-${APP_ENV}/auth/company.id`;

const initialState = {
  products: productsInstance.initialState,
  user: null,
  companyId: null,
  company: companyInstance.initialState,
  fetchLoading: false,
  fetchFailed: false,
};

const reducers = {
  ...companyInstance.reducersMap,
  ...productsInstance.reducersMap,

  [SET_CURRENT_USER]: (state, { user, auth_token }) => ({
    ...state,
    user,
  }),

  [CHANGE_CURRENT_COMPANY]: (state, { companyId }) => ({
    ...state,
    companyId,
    company: state.company.model && state.company.model.id === companyId ? state.company : companyInstance.initialState,
  }),

  [FETCH_CURRENT_USER]: (state) => ({
    ...state,
    fetchLoading: true,
    fetchFailed: false,
  }),

  [FETCH_CURRENT_USER_SUCCESS]: (state, { user }) => ({
    ...state,
    user,
    fetchLoading: false,
  }),

  [FETCH_CURRENT_USER_FAILURE]: (state) => ({
    ...state,
    fetchLoading: false,
    fetchFailed: true,
  }),

  [LOGOUT]: (state) => initialState,
};

const epics = [
  ...companyInstance.epics,
  ...productsInstance.epics,

  (actions$) =>
    actions$
      .ofType(INITIALIZE_APP)
      .filter(({ reset }) => reset)
      .flatMap(() => {
        const email = window.localStorage.getItem(CURRENT_USER_EMAIL_CACHE_KEY);
        const authToken = window.localStorage.getItem(CURRENT_USER_AUTH_TOKEN_CACHE_KEY);
        const currentCompanyId = window.localStorage.getItem(CURRENT_COMPANY_ID_CACHE_KEY);

        let obs$ = Observable.concat();
        if (email && authToken) {
          obs$ = obs$.concat(
            Observable.of(setCurrentUser({ user: { email }, auth_token: authToken })),
            Observable.of(fetchCurrentUser()).delay(100)
          );
        }
        if (currentCompanyId) {
          obs$ = obs$.concat(Observable.of(changeCurrentCompany(+currentCompanyId)));
        }

        return obs$;
      }),

  (actions$) =>
    actions$.ofType(SET_CURRENT_USER).flatMap(({ user, auth_token }) => {
      if (user && auth_token) {
        try {
          window.localStorage.setItem(CURRENT_USER_EMAIL_CACHE_KEY, user.email);
          window.localStorage.setItem(CURRENT_USER_AUTH_TOKEN_CACHE_KEY, auth_token);
        } catch (e) {
          if (__DEV__) {
            throw e;
          }
        }
        ApiService.setCurrentAdminUser(user.email, auth_token);
      } else {
        window.localStorage.removeItem(CURRENT_USER_EMAIL_CACHE_KEY);
        window.localStorage.removeItem(CURRENT_USER_AUTH_TOKEN_CACHE_KEY);
        ApiService.setCurrentAdminUser(null, null);
      }

      return Observable.empty();
    }),

  // Set current company
  (actions$, { getState }) =>
    actions$
      .ofType(SET_CURRENT_USER)
      .merge(actions$.ofType(FETCH_CURRENT_USER_SUCCESS))
      .merge(actions$.ofType(LOGOUT))
      .map(() => {
        const { user, companyId } = stateSelector(getState());
        const nextCompanyId =
          user && user.companies
            ? ((companyId && user.companies.find((c) => c.id === companyId)) || user.companies[0] || { id: null }).id
            : companyId;
        return changeCurrentCompany(nextCompanyId);
      }),

  // Save the current company id in the localStorage and ApiService
  (actions$, { getState }) =>
    actions$.ofType(CHANGE_CURRENT_COMPANY).flatMap(({ companyId }) => {
      if (companyId) {
        try {
          window.localStorage.setItem(CURRENT_COMPANY_ID_CACHE_KEY, companyId);
        } catch (e) {
          if (__DEV__) {
            throw e;
          }
        }
        ApiService.setCurrentCompany(companyId);
      } else {
        ApiService.setCurrentCompany(null);
      }
      return Observable.empty();
    }),

  // Fetch current company data (debounce to avoid duplicate requests
  // when CHANGE_CURRENT_COMPANY fires multiple times during navigation)
  (actions$, { getState }) =>
    actions$.ofType(CHANGE_CURRENT_COMPANY).debounceTime(100).flatMap(({ companyId }) => {
      const { user } = stateSelector(getState());
      if (user && user.companies && companyId) {
        return Observable.of(fetchCurrentCompany());
      } else {
        return Observable.empty();
      }
    }),

  // Fetch current company products (debounce to avoid duplicate requests
  // when CHANGE_CURRENT_COMPANY fires multiple times during navigation)
  (actions$, { getState }) =>
    actions$.ofType(CHANGE_CURRENT_COMPANY).debounceTime(100).flatMap(({ companyId }) => {
      const { user } = stateSelector(getState());
      if (user && user.companies && companyId) {
        return Observable.of(fetchProducts());
      } else {
        return Observable.empty();
      }
    }),

  // Update Raven user context data
  (actions$, { getState }) =>
    actions$
      .ofType(SET_CURRENT_USER)
      .merge(actions$.ofType(FETCH_CURRENT_USER_SUCCESS))
      .merge(actions$.ofType(LOGOUT))
      .flatMap(() => {
        const { user } = stateSelector(getState());
        if (user) {
          window.Raven &&
            Raven.setUserContext({
              id: user.id,
              email: user.email,
              username: user.email,
            });
        } else {
          window.Raven && Raven.setUserContext(null);
        }
        return Observable.empty();
      }),

  // Change moment's default timezone
  (actions$, { getState }) =>
    actions$
      .ofType(SET_CURRENT_USER)
      .merge(actions$.ofType(FETCH_CURRENT_USER_SUCCESS))
      .merge(actions$.ofType(LOGOUT))
      .flatMap(() => {
        const { user } = stateSelector(getState());
        if (user && user.time_zone_name && (!moment.defaultZone || moment.defaultZone.name !== user.time_zone_name)) {
          moment.tz.setDefault(user.time_zone_name);
          return Observable.of({ type: "redux-reload" });
        } else if (!moment.defaultZone) {
          moment.tz.guess();
          return Observable.of({ type: "redux-reload" });
        } else {
          return Observable.empty();
        }
      }),

  // Change I18n.locale to user's locale
  (actions$, { getState }) =>
    actions$
      .ofType(SET_CURRENT_USER)
      .merge(actions$.ofType(FETCH_CURRENT_USER_SUCCESS))
      .flatMap(() => {
        const { user } = stateSelector(getState());
        if (user && user.locale && I18n.locale !== user.locale) {
          setLocale(user.locale);
          try {
            window.localStorage.setItem(LOCALE_CACHE_KEY, I18n.locale);
          } catch (e) {
            if (__DEV__) {
              throw e;
            }
          }
          return Observable.of({ type: "redux-reload" });
        } else {
          return Observable.empty();
        }
      }),

  // Fetch the user
  (actions$, store) =>
    actions$.ofType(FETCH_CURRENT_USER).switchMap(() => {
      return AccountRepository.show()
        .map((user) => {
          return { type: FETCH_CURRENT_USER_SUCCESS, user };
        })
        .catch((error) => {
          st.api.handleError(error, store);
          if (error.response && error.response.status === 401) {
            return Observable.concat(
              Observable.of({ type: FETCH_CURRENT_USER_FAILURE, error }),
              Observable.of({ type: LOGOUT }),
              Observable.of(
                createAlert({
                  uuid: FETCH_CURRENT_USER_FAILURE,
                  type: "error",
                  content: I18n.t("papp.auth.index.fetch_user_failed_message"),
                })
              )
            );
          } else {
            return Observable.concat(
              Observable.of({ type: FETCH_CURRENT_USER_FAILURE, error }),
              Observable.of(
                createAlert({
                  uuid: FETCH_CURRENT_USER_FAILURE,
                  type: "error",
                  content: st.api.findResponseErrorMessage(error),
                  timeout: false,
                })
              )
            );
          }
        });
    }),

  // The local logout must not wait for, or depend on, the server revoking the token
  (actions$) =>
    actions$.ofType(LOGOUT).flatMap(({ revokeToken }) => {
      const clearCurrentUser$ = Observable.of(setCurrentUser());
      if (!revokeToken) {
        return clearCurrentUser$;
      }
      const revoke$ = AccountRepository.logout()
        .ignoreElements()
        .catch(() => Observable.empty());
      return Observable.merge(revoke$, clearCurrentUser$);
    }),
];

export default st.duck.createDuck(initialState, reducers, epics, {
  LoginView: LoginViewDuck,
  SendResetPasswordInstructionsView: SendResetPasswordInstructionsViewDuck,
  ResetPasswordView: ResetPasswordViewDuck,
  SendConfirmationInstructionsView: SendConfirmationInstructionsViewDuck,
  ConfirmEmailView: ConfirmEmailViewDuck,
  SendUnlockInstructionsView: SendUnlockInstructionsViewDuck,
  UnlockAccessView: UnlockAccessViewDuck,
});



// WEBPACK FOOTER //
// ./src/modules/auth/index.js